"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSubmitContracts = void 0;
const ui_1 = require("@app/component/ui");
const user_details_1 = require("@app/reducer/user-details");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const promises_1 = require("@app/util/promises");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hot_toast_1 = __importDefault(require("react-hot-toast"));
const create_contract_1 = require("../../request/create-contract");
const locality_assign_1 = require("../../request/locality-assign");
const locality_delete_1 = require("../../request/locality-delete");
const position_assign_1 = require("../../request/position-assign");
const position_delete_1 = require("../../request/position-delete");
const user_details_contracts_1 = require("../../request/user-details-contracts");
const employee_card_context_1 = require("../employee-card-context");
const ignoreNewContract = (contracts) => {
    return contracts.splice(1);
};
const useSubmitContracts = (contracts, editing) => {
    const dispatch = (0, store_1.useRootDispatch)();
    const { userId, workspace } = (0, employee_card_context_1.useEmployeeCard)();
    const localities = workspace?.localities ?? [];
    const handleLocalities = async (updatedContracts) => {
        const diffs = {
            added: [],
            removed: [],
        };
        const assignedLocalities = localities.filter((l) => l?.assigns?.some((a) => a.userId === userId));
        const nonAssignedLocalities = localities.filter((l) => !l?.assigns?.some((a) => a.userId === userId));
        // updatedContracts.forEach(c => console.log({ contractLocalities: c.localities, assignedLocalities, nonAssignedLocalities }))
        const added = updatedContracts
            .flatMap((uc) => nonAssignedLocalities.filter((nal) => uc.localities?.some((l) => l.id === nal?.id)))
            .map((l) => l?.id ?? '');
        const removed = updatedContracts
            .flatMap((uc) => assignedLocalities.filter((al) => !uc.localities?.some((l) => l.id === al?.id)))
            .map((l) => l?.id ?? '');
        diffs.added.push(...added);
        diffs.removed.push(...removed);
        const addLocalities = diffs.added.map((l) => async () => {
            await (0, locality_assign_1.mutateLocalityAssign)({
                workspaceId: workspace?.id ?? '',
                userId: userId ?? '',
                localityId: l,
            });
        });
        const removeLocalities = diffs.removed.map((l) => async () => {
            await (0, locality_delete_1.mutateLocalityAssignDelete)({
                workspaceId: workspace?.id ?? '',
                userId: userId ?? '',
                localityId: l,
            });
        });
        const updateLocalities = [...addLocalities, ...removeLocalities];
        for await (const update of updateLocalities) {
            await update();
        }
    };
    // Change this to updatedContracts: [Contract, Positions][]
    // Positions should be added to contract before saving to state based on results from mutations
    // eg: a mutation could fail but the position is assigned currently to the contract in state
    const handlePositions = async (updatedContracts) => {
        const positionDifferences = updatedContracts.map((currentContract) => {
            const diffs = {
                id: (0, maybe_unwrap_1.unwrapMaybe)(currentContract.id),
                added: [],
                removed: [],
            };
            const originalContract = contracts?.find((c) => c.id === currentContract.id);
            const currentSet = new Set(currentContract.positions?.map((p) => p.positionId));
            const originalSet = new Set(originalContract?.positions?.map((p) => p.positionId));
            const added = currentContract.positions
                ?.filter((p) => !originalSet.has(p.positionId))
                .map((p) => p.positionId) ?? [];
            const removed = originalContract?.positions
                ?.filter((p) => !currentSet.has(p.positionId))
                .map((p) => p.positionId) ?? [];
            diffs.added.push(...added);
            diffs.removed.push(...removed);
            return diffs;
        });
        const addedPositions = positionDifferences.flatMap((c) => c.added.map((p) => async () => await (0, position_assign_1.mutatePositionAssign)({
            workspaceId: workspace?.id ?? '',
            userId: userId ?? '',
            positionId: p,
            contractId: c.id ?? '',
        })));
        const removedPositions = positionDifferences.flatMap((c) => c.removed.map((p) => async () => await (0, position_delete_1.mutatePositionAssignDelete)({
            workspaceId: workspace?.id ?? '',
            userId: userId ?? '',
            positionId: p,
            contractId: c.id ?? '',
        })));
        const updatePositions = [...addedPositions, ...removedPositions];
        for await (const update of updatePositions) {
            await update();
        }
    };
    const submit = async (data) => {
        if (editing.includes('new')) {
            const newContract = data.contracts[0];
            const createdContract = await (0, create_contract_1.mutateCreateContract)({
                workspaceId: workspace?.id ?? '',
                userId: userId ?? '',
                data: {
                    contractId: (0, maybe_unwrap_1.unwrapMaybe)(newContract.contractId),
                    type: newContract.type ?? '',
                    period: {
                        start: (0, moment_1.default)(newContract.period?.start, 'YYYY-MM-DD')
                            .startOf('day')
                            .toISOString(),
                        end: (0, moment_1.default)(newContract.period?.end, 'YYYY-MM-DD')
                            .endOf('day')
                            .toISOString(),
                    },
                    // positionIds is not supported anymore in the mutation
                    // positionIds:
                    // newContract.positions?.map((position) => position.positionId).filter(isDefined) ?? [],
                    options: newContract.options,
                },
            });
            if ((0, util_1.isUndefined)(createdContract))
                return;
            createdContract.positions = newContract.positions
                ?.map((position) => ({
                positionId: position.positionId,
                contractId: createdContract?.id,
                created: null,
                skill: 0,
            }))
                .filter(util_1.isDefined);
            await handlePositions([createdContract]);
            if ((0, util_1.isDefined)(createdContract)) {
                dispatch((0, user_details_1.updatedUserContract)(createdContract));
            }
            return;
        }
        const updatedContracts = ignoreNewContract(data.contracts).filter((contract) => editing.includes(contract.cId ?? ''));
        const variables = updatedContracts.map((contract) => ({
            workspaceId: workspace?.id ?? '',
            userId: userId ?? '',
            id: contract.id ?? '',
            data: {
                contractId: (0, maybe_unwrap_1.unwrapMaybe)(contract.contractId),
                type: contract.type ?? '',
                period: {
                    start: (0, moment_1.default)(contract.period?.start).startOf('day').toISOString(),
                    end: (0, moment_1.default)(contract.period?.end).endOf('day').toISOString(),
                },
                positionIds: contract.positions
                    ?.map((position) => position.positionId)
                    .filter(util_1.isDefined) ?? [],
                options: contract.options,
            },
        }));
        await handlePositions(updatedContracts);
        await handleLocalities(updatedContracts);
        const contractsChanged = variables.map(async (variables) => await (0, user_details_contracts_1.mutateUserDetailsContract)(variables));
        const resultsSettled = await Promise.allSettled(contractsChanged);
        const fulfilledResults = resultsSettled.filter(promises_1.isFulfilled);
        const rejectedResults = resultsSettled.filter(promises_1.isRejected);
        const successfulResults = fulfilledResults.map((e) => e.value).filter(util_1.isDefined);
        dispatch((0, user_details_1.updatedUserContracts)(successfulResults));
        const totalUpdated = resultsSettled.length;
        const totalFulfilled = fulfilledResults.length;
        const totalRejected = rejectedResults.length;
        if (totalFulfilled === totalUpdated) {
            react_hot_toast_1.default.custom(() => (react_1.default.createElement(ui_1.Alert, { type: 'SUCCESS', text: `${(0, util_1.t)('SUCCESS_MESSAGE.employeeProfile')} ${totalFulfilled}/${totalUpdated}` })));
        }
        else if (totalRejected === totalUpdated) {
            react_hot_toast_1.default.custom(() => (react_1.default.createElement(ui_1.Alert, { type: 'ERROR', text: `${(0, util_1.t)('ERROR_SCREEN_TITLE')} ${totalRejected}/${totalUpdated}` })));
        }
        else {
            react_hot_toast_1.default.custom(() => (react_1.default.createElement(ui_1.Alert, { type: 'WARNING', text: `${(0, util_1.t)('ERROR_SCREEN_TITLE')} ${totalRejected}/${totalUpdated}` })));
        }
    };
    return submit;
};
exports.useSubmitContracts = useSubmitContracts;
